import { Box } from "@chakra-ui/react";
import { SectionWrapper } from "../SectionWrapper";
import type { HTMLAttributes } from "react";
import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface Props extends HTMLAttributes<HTMLDivElement> {
  revenueData: {
    revenue: number | string | null | undefined;
    name: string;
  }[];
}

// Constants to prevent inline object creation
const CHART_MARGIN = { left: 4, bottom: 0 };
const BAR_SIZE = 40;
const BAR_RADIUS: [number, number, number, number] = [5, 5, 0, 0];
const REVENUE_COLOR = "#3182ce";
const FONT_SIZE_14 = 14;
const FONT_SIZE_10 = 10;
const MIN_HEIGHT = "200px";
const MIN_MAX_VALUE = 1000;

// Utility functions extracted outside component
const parseNumericValue = (value: number | string | null | undefined): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return parseFloat(value);
  return 0;
};

const formatLabel = (name: string): string => {
  const words: string[] = name.match(/^[a-z]+|[A-Z][a-z]+/gm) ?? [];
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const formatTick = (value: number): string => {
  return "$" + value.toLocaleString("en-US");
};

function SectionFinancialsRevenue({ revenueData, ...props }: Props) {
  const chartData = React.useMemo(
    () =>
      revenueData.map((column) => ({
        revenue: parseNumericValue(column.revenue),
        name: column.name,
      })),
    [revenueData]
  );

  const maxValue = React.useMemo(() => Math.max(MIN_MAX_VALUE, ...chartData.map((col) => col.revenue)), [chartData]);

  const labelFormatter = React.useCallback(formatLabel, []);
  const tickFormatter = React.useCallback(formatTick, []);

  return (
    <SectionWrapper title="Revenue (CAD)" {...props}>
      <Box height="max-content" pr="1rem">
        <ResponsiveContainer width="100%" height="100%" minHeight={MIN_HEIGHT}>
          <BarChart width={500} height={500} data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="name" fontSize={FONT_SIZE_14} tickLine={false} />
            <YAxis tickLine={false} domain={[0, maxValue]} tickFormatter={tickFormatter} fontSize={FONT_SIZE_10} />
            <Tooltip formatter={tickFormatter} labelFormatter={labelFormatter} />
            <Bar dataKey="revenue" fill={REVENUE_COLOR} barSize={BAR_SIZE} radius={BAR_RADIUS} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionWrapper>
  );
}

export default React.memo(SectionFinancialsRevenue);
